/**
 * Supplier quotations for one lead. Shown on the lead, where the prices are
 * gathered, and again on the sales order the lead became, where they are
 * turned into purchase orders.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Paperclip, Plus, Trash2 } from 'lucide-react';
import { ApiError, api, getAccessToken } from '../../lib/api';
import {
  EMPTY_LINE,
  LineItemsEditor,
  filledLines,
  linesTotal,
  type DraftLine,
} from './LineItemsEditor';
import { useAuth } from '../../lib/auth';
import { peso, shortDate } from '../../lib/format';
import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Skeleton,
  StatusChip,
  TableWrap,
  Td,
  Th,
  inputClass,
} from '../../components/ui';

export interface QuotationItem {
  id: string;
  lineNo: number;
  particular: string;
  unit: string | null;
  qty: string;
  unitPrice: string;
  amount: string;
}

export interface Quotation {
  id: string;
  items: QuotationItem[];
  quoteRef: string | null;
  quoteDate: string;
  validUntil: string | null;
  description: string | null;
  amount: string;
  vatIncluded: boolean;
  status: 'RECEIVED' | 'SELECTED' | 'DECLINED';
  supplier: { id: string; code: string; name: string; status: string };
  file: { id: string; originalName: string } | null;
  uploader: { name: string };
  _count: { purchaseOrders: number };
}

interface SupplierOption {
  id: string;
  name: string;
  code: string;
}

const STATUS_TONE = {
  RECEIVED: 'neutral',
  SELECTED: 'ok',
  DECLINED: 'warning',
} as const;

const STATUS_LABEL = {
  RECEIVED: 'Received',
  SELECTED: 'Selected',
  DECLINED: 'Declined',
} as const;

export function downloadQuotation(quotationId: string, name: string): void {
  void (async () => {
    const response = await fetch(`/api/v1/quotations/${quotationId}/file`, {
      credentials: 'include',
      headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) return;
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
  })();
}

export function QuotationsCard({
  leadId,
  readOnly = false,
}: {
  leadId: string;
  /** The sales order view lists them; the lead is where they are edited. */
  readOnly?: boolean;
}): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([{ ...EMPTY_LINE }]);
  const [draft, setDraft] = useState({
    supplierId: '',
    quoteRef: '',
    quoteDate: new Date().toISOString().slice(0, 10),
    validUntil: '',
    description: '',
    amount: '',
    vatIncluded: 'true',
  });

  const quotations = useQuery({
    queryKey: ['quotations', leadId],
    queryFn: () => api<{ data: Quotation[] }>(`/leads/${leadId}/quotations`),
  });

  const suppliers = useQuery({
    queryKey: ['suppliers', 'options'],
    queryFn: () =>
      api<{ data: SupplierOption[] }>('/suppliers?status=ACTIVE&pageSize=200&sort=name'),
    enabled: adding,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['quotations'] });
  };

  const add = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      const items = filledLines(lines);
      body.append('supplierId', draft.supplierId);
      body.append('quoteDate', draft.quoteDate);
      // With lines, the total is theirs; without, the single figure typed in.
      body.append('amount', items.length > 0 ? String(linesTotal(lines)) : draft.amount);
      if (items.length > 0) body.append('items', JSON.stringify(items));
      body.append('vatIncluded', draft.vatIncluded);
      if (draft.quoteRef.trim()) body.append('quoteRef', draft.quoteRef.trim());
      if (draft.validUntil) body.append('validUntil', draft.validUntil);
      if (draft.description.trim()) body.append('description', draft.description.trim());
      if (file) body.append('file', file);

      const response = await fetch(`/api/v1/leads/${leadId}/quotations`, {
        method: 'POST',
        credentials: 'include',
        headers: { authorization: `Bearer ${getAccessToken() ?? ''}` },
        body,
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new ApiError(response.status, payload?.message ?? 'Could not save the quotation.');
      }
      return response.json();
    },
    onSuccess: () => {
      invalidate();
      setAdding(false);
      setFile(null);
      setError(null);
      setLines([{ ...EMPTY_LINE }]);
      setDraft({ ...draft, supplierId: '', quoteRef: '', description: '', amount: '' });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save the quotation.'),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Quotation['status'] }) =>
      api(`/quotations/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: invalidate,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not update the quotation.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/quotations/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not remove the quotation.'),
  });

  const rows = quotations.data?.data ?? [];
  const canEdit = !readOnly && can('quotations.manage');
  // Worth seeing at a glance when several suppliers quoted the same job.
  const cheapest = rows.length > 1 ? Math.min(...rows.map((row) => Number(row.amount))) : null;

  return (
    <Card
      title={`Supplier quotations${rows.length ? ` (${rows.length})` : ''}`}
      description="What each supplier wants for this job. Selecting one commits nothing — the purchase order does."
    >
      {error && (
        <div className="mb-3">
          <Alert tone="critical">{error}</Alert>
        </div>
      )}

      {quotations.isLoading && <Skeleton className="h-24 w-full" />}

      {!quotations.isLoading && rows.length === 0 && (
        <EmptyState
          title="No quotations yet"
          description={
            canEdit
              ? 'Add what each supplier quoted, with their PDF if you have it.'
              : 'Nothing has been quoted for this job.'
          }
        />
      )}

      {rows.length > 0 && (
        <TableWrap>
          <thead>
            <tr>
              <Th>Supplier</Th>
              <Th>Reference</Th>
              <Th>Quoted</Th>
              <Th align="right">Amount</Th>
              <Th>Status</Th>
              <Th>{''}</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>
                  <p className="font-semibold">{row.supplier.name}</p>
                  {row.description && (
                    <p className="text-xs text-slate-500">{row.description}</p>
                  )}
                </Td>
                <Td>
                  <p className="text-xs">{row.quoteRef ?? '—'}</p>
                  {row.file && (
                    <button
                      type="button"
                      className="mt-0.5 inline-flex items-center gap-1 text-xs text-brand hover:underline"
                      onClick={() => downloadQuotation(row.id, row.file!.originalName)}
                    >
                      <Download aria-hidden="true" className="h-3 w-3" />
                      {row.file.originalName}
                    </button>
                  )}
                </Td>
                <Td>
                  <p className="text-xs">{shortDate(row.quoteDate)}</p>
                  {row.validUntil && (
                    <p className="text-xs text-slate-500">until {shortDate(row.validUntil)}</p>
                  )}
                </Td>
                <Td align="right">
                  <span
                    className={
                      cheapest !== null && Number(row.amount) === cheapest
                        ? 'font-semibold text-emerald-700 dark:text-emerald-300'
                        : undefined
                    }
                  >
                    {peso(row.amount)}
                  </span>
                  <span className="block text-xs text-slate-500">
                    {row.vatIncluded ? 'VAT included' : 'plus VAT'}
                  </span>
                </Td>
                <Td>
                  <StatusChip tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</StatusChip>
                  {row._count.purchaseOrders > 0 && (
                    <span className="mt-1 block text-xs text-slate-500">
                      {row._count.purchaseOrders} PO
                      {row._count.purchaseOrders === 1 ? '' : 's'} raised
                    </span>
                  )}
                </Td>
                <Td>
                  {canEdit && (
                    <div className="flex items-center justify-end gap-1">
                      <select
                        className={`${inputClass} h-9 w-32 py-0 text-xs`}
                        value={row.status}
                        aria-label={`Status of ${row.supplier.name}'s quotation`}
                        onChange={(event) =>
                          setStatus.mutate({
                            id: row.id,
                            status: event.target.value as Quotation['status'],
                          })
                        }
                      >
                        <option value="RECEIVED">Received</option>
                        <option value="SELECTED">Selected</option>
                        <option value="DECLINED">Declined</option>
                      </select>
                      <button
                        type="button"
                        className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 dark:hover:bg-slate-800"
                        aria-label={`Remove ${row.supplier.name}'s quotation`}
                        title={
                          row._count.purchaseOrders > 0
                            ? 'A purchase order was raised from this quotation'
                            : 'Remove'
                        }
                        disabled={row._count.purchaseOrders > 0}
                        onClick={() => remove.mutate(row.id)}
                      >
                        <Trash2 aria-hidden="true" className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {canEdit && (
        <div className="mt-3">
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
            <Plus aria-hidden="true" className="h-4 w-4" /> Add quotation
          </Button>
        </div>
      )}

      {adding && (
        <Modal
          title="Add supplier quotation"
          description="The PDF is optional — a price given over the phone is still worth recording."
          onClose={() => {
            setAdding(false);
            setFile(null);
            setError(null);
          }}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              setError(null);
              add.mutate();
            }}
          >
            {error && <Alert tone="critical">{error}</Alert>}
            <Field label="Supplier" required>
              <select
                className={inputClass}
                value={draft.supplierId}
                onChange={(event) => setDraft({ ...draft, supplierId: event.target.value })}
                required
              >
                <option value="">Choose a supplier…</option>
                {suppliers.data?.data.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Their quotation no.">
                <input
                  className={inputClass}
                  value={draft.quoteRef}
                  onChange={(event) => setDraft({ ...draft, quoteRef: event.target.value })}
                />
              </Field>
              <Field label="Quotation date" required>
                <input
                  className={inputClass}
                  type="date"
                  value={draft.quoteDate}
                  onChange={(event) => setDraft({ ...draft, quoteDate: event.target.value })}
                  required
                />
              </Field>
              <Field
                label="Amount"
                hint={
                  filledLines(lines).length > 0
                    ? 'Taken from the lines below'
                    : 'Or list the items below instead'
                }
              >
                <input
                  className={inputClass}
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={
                    filledLines(lines).length > 0 ? String(linesTotal(lines)) : draft.amount
                  }
                  disabled={filledLines(lines).length > 0}
                  onChange={(event) => setDraft({ ...draft, amount: event.target.value })}
                />
              </Field>
              <Field label="That amount is">
                <select
                  className={inputClass}
                  value={draft.vatIncluded}
                  onChange={(event) => setDraft({ ...draft, vatIncluded: event.target.value })}
                >
                  <option value="true">VAT inclusive</option>
                  <option value="false">VAT exclusive</option>
                </select>
              </Field>
              <Field label="Valid until">
                <input
                  className={inputClass}
                  type="date"
                  value={draft.validUntil}
                  onChange={(event) => setDraft({ ...draft, validUntil: event.target.value })}
                />
              </Field>
            </div>
            <LineItemsEditor
              lines={lines}
              onChange={setLines}
              vatIncluded={draft.vatIncluded === 'true'}
              label="What they are quoting (optional)"
            />

            <Field label="Summary of the job">
              <textarea
                className={inputClass}
                rows={2}
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </Field>
            <Field label="Their quotation file" hint="PDF or a photo of it, up to 15 MB">
              <input
                className={inputClass}
                type="file"
                accept="application/pdf,image/*"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </Field>
            {file && (
              <p className="flex items-center gap-1.5 text-xs text-slate-500">
                <Paperclip aria-hidden="true" className="h-3 w-3" /> {file.name}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setAdding(false);
                  setFile(null);
                  setError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  add.isPending ||
                  !draft.supplierId ||
                  (filledLines(lines).length === 0 && !draft.amount)
                }
              >
                <FileText aria-hidden="true" className="h-4 w-4" /> Save quotation
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </Card>
  );
}
